import { z } from "zod";
import type { PlatformStats } from "@/lib/types/stats";

// GET /stats/platform is a legacy route without the standard envelope. The
// public pages render it to anonymous visitors, so the figures they read are
// checked here before anything is shown; a malformed payload is an error
// (the page shows "Try again"), never a wrong number.

const count = z.number().finite().nonnegative();
const named = z.object({ name: z.string(), value: count });

export const platformStatsSchema = z.object({
  definitionVersion: z.string().optional(),
  generatedAt: z.string(),
  audio: z.object({
    total: count,
    accepted: count,
    pending: count,
    rejected: count,
    thisMonth: count,
    recordedHours: count,
    initialDataHours: count.optional(),
    validatedHours: count.optional(),
  }).passthrough(),
  users: z.object({
    registered: count,
    activeContributors: count,
    suspended: count,
    banned: count,
    staff: count,
  }).passthrough().optional(),
  initialData: z.unknown().optional(),
  contributors: z.object({
    total: count,
    active: count.optional(),
    countries: count,
    byCountry: z.array(named.passthrough()),
    ageBuckets: z.array(named.passthrough()),
    averageAge: z.number().finite().nullable(),
  }).passthrough(),
  collection: z.object({
    byCountry: z.array(z.object({ countryCode: z.string().nullable(), name: z.string(), value: count }).passthrough()),
    byCity: z.array(z.object({ city: z.string(), countryCode: z.string(), value: count }).passthrough()),
  }).passthrough().optional(),
  languages: z.object({
    active: count,
    withContributions: count,
    byLanguage: z.array(z.object({ code: z.string(), name: z.string(), total: count, accepted: count }).passthrough()),
  }).passthrough(),
}).passthrough();

export function parsePlatformStats(payload: unknown): PlatformStats {
  return platformStatsSchema.parse(payload) as PlatformStats;
}
